import { ChevronDown, KeyRound, LogOut, Menu, Search, CircleUser, X, Sun, Moon, Monitor, Keyboard, Sparkles, Compass, SlidersHorizontal } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Dialog, DropdownMenu } from 'radix-ui'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/cn'
import { Breadcrumbs, type BreadcrumbItem } from '@/components/common/Breadcrumbs'
import { NAV } from '@/lib/nav'
import { BrandMark, NavContent } from './Sidebar'
import { CommandPalette } from './CommandPalette'
import { NotificationCentre } from './NotificationCentre'
import { ActAsOrganisationButton } from '@/components/security/SupportAccess'
import { usePreferences, type ThemePref } from '@/lib/preferences'
import { useToast } from '@/hooks/useToast'
import { errorMessage } from '@/lib/apiError'
import { useHasUnseenNews } from './WelcomeAndNews'

const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: 'Super admin', HR_ADMIN: 'HR admin', HR_EXECUTIVE: 'HR executive',
  MANAGER: 'Manager', EMPLOYEE: 'Employee', CA_PARTNER: 'CA partner',
}

// Breadcrumb labels come from the navigation register, so a page is named the same in the
// sidebar, the palette and the breadcrumb. Segments with no page of their own (e.g. "/ess",
// ids) are rendered as plain text, never as a link to a route that does not exist.
// First entry wins: a path listed twice (a My-space shortcut for one role) keeps its main label.
const LABEL_BY_PATH: Record<string, string> = Object.fromEntries(
  NAV.flatMap((s) => s.items.filter((i) => !i.audience).map((i) => [i.to, i.label])),
)
const EXTRA: Record<string, string> = { '/ess': 'My space', '/settings': 'Settings', '/admin/cms': 'Website CMS', '/notifications': 'Notifications' }
const isId = (seg: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg) || /^\d+$/.test(seg)

function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const parts = pathname.split('/').filter(Boolean)
  const crumbs: BreadcrumbItem[] = []
  let href = ''
  parts.forEach((seg, i) => {
    href += `/${seg}`
    const last = i === parts.length - 1
    const known = LABEL_BY_PATH[href] ?? EXTRA[href]
    const label = isId(seg) ? 'Details' : known ?? seg.replace(/[-_]/g, ' ').replace(/^\w/, (m) => m.toUpperCase())
    crumbs.push({ label, href: !last && LABEL_BY_PATH[href] ? href : undefined })
  })
  return crumbs
}

const menuItem =
  'flex h-9 cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 text-sm text-foreground outline-none transition-colors duration-fast data-[highlighted]:bg-surface-hover'

interface TopbarProps {
  onShortcuts?: () => void
  onWelcome?: () => void
  onWhatsNew?: () => void
}

const THEMES: { value: ThemePref; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'Match my device', icon: Monitor },
]

export function Topbar({ onShortcuts, onWelcome, onWhatsNew }: TopbarProps = {}) {
  const { user, logout } = useAuth()
  const { prefs, update } = usePreferences()
  const toast = useToast()
  const unseenNews = useHasUnseenNews()
  const setTheme = (t: ThemePref) => {
    update({ theme: t }).catch((e) => toast.error(errorMessage(e), { title: 'Theme not saved' }))
  }
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const crumbs = useMemo(() => buildBreadcrumbs(pathname), [pathname])

  const name = user ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() : ''
  const initials = user ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase() : ''
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform)

  async function handleLogout() {
    try { await logout() } finally { navigate('/login') }
  }

  return (
    <header data-print-hide className="relative z-40 flex h-14 shrink-0 print:hidden items-center gap-2 border-b border-border bg-card px-3  sm:gap-3 sm:px-5">
      {/* Mobile: menu drawer (the desktop rail is hidden below md) */}
      <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
        <Dialog.Trigger asChild>
          <button type="button" className="grid size-9 place-items-center rounded-md text-content-secondary hover:bg-surface-hover md:hidden" aria-label="Open menu">
            <Menu className="size-5" />
          </button>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[60] bg-overlay/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed inset-y-0 left-0 z-[70] flex w-[280px] max-w-[85vw] flex-col border-r border-[rgb(var(--sb-line))] bg-[rgb(var(--sb-bg))] shadow-e4 data-[state=open]:animate-in data-[state=open]:slide-in-from-left data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left duration-slow"
          >
            <Dialog.Title className="sr-only">Navigation</Dialog.Title>
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-[rgb(var(--sb-line))] px-4">
              <BrandMark expanded />
              <Dialog.Close className="grid size-8 place-items-center rounded-md text-[rgb(var(--sb-label))] hover:bg-[rgb(var(--sb-hover))] hover:text-[rgb(var(--sb-brand))]" aria-label="Close menu">
                <X className="size-4" />
              </Dialog.Close>
            </div>
            <NavContent expanded onNavigate={() => setDrawerOpen(false)} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="hidden min-w-0 flex-1 md:block">
        {crumbs.length > 0 && <Breadcrumbs items={crumbs} />}
      </div>
      <div className="flex-1 md:hidden" />

      {/* Page search trigger */}
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        className="group flex h-9 items-center gap-2 rounded-md border border-border bg-background/60 pl-2.5 pr-1.5 text-sm text-content-placeholder shadow-control transition-colors duration-fast hover:border-input/50 hover:bg-card sm:w-64"
        aria-label="Search pages, people and records"
      >
        <Search className="size-4 shrink-0 text-muted-foreground" />
        <span className="hidden flex-1 text-left sm:inline">Search…</span>
        <kbd className="hidden rounded border border-border bg-card px-1.5 py-px text-[11px] font-medium text-muted-foreground sm:inline">
          {isMac ? '⌘' : 'Ctrl'} K
        </kbd>
      </button>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} onShortcuts={onShortcuts} />

      <ActAsOrganisationButton />
      <NotificationCentre enabled={Boolean(user)} />

      <div aria-hidden className="mx-0.5 hidden h-6 w-px bg-border sm:block" />

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            aria-label="Account menu"
            className="flex items-center gap-2.5 rounded-md py-1 pl-1 pr-1.5 text-left transition-colors duration-fast hover:bg-surface-hover data-[state=open]:bg-surface-hover"
          >
            <span className="relative grid size-8 shrink-0 place-items-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
              {initials || <CircleUser className="size-4" />}
              {unseenNews && <span aria-hidden className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-info ring-2 ring-card" />}
            </span>
            <span className="hidden min-w-0 leading-tight lg:block">
              <span className="block max-w-[160px] truncate text-[13px] font-medium text-foreground">{name}</span>
              <span className="block text-xs text-muted-foreground">{ROLE_LABEL[user?.role ?? ''] ?? user?.role}</span>
            </span>
            <ChevronDown className="hidden size-4 text-muted-foreground lg:block" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={8}
            className="z-50 w-60 rounded-lg border border-border bg-popover p-1.5 shadow-e3 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[side=bottom]:slide-in-from-top-1"
          >
            <div className="px-2.5 pb-2 pt-1.5">
              <div className="truncate text-sm font-medium text-foreground">{name || 'Signed in'}</div>
              <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
            </div>
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            <DropdownMenu.Item asChild className={menuItem}>
              <Link to="/ess/profile"><CircleUser className="size-4 text-muted-foreground" />My profile</Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item asChild className={menuItem}>
              <Link to="/ess/profile?tab=security"><KeyRound className="size-4 text-muted-foreground" />Password &amp; security</Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item asChild className={menuItem}>
              <Link to="/ess/profile?tab=preferences"><SlidersHorizontal className="size-4 text-muted-foreground" />Preferences</Link>
            </DropdownMenu.Item>
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            <DropdownMenu.Label className="px-2.5 pb-1 pt-1.5 text-[11px] font-medium text-muted-foreground">Theme</DropdownMenu.Label>
            <DropdownMenu.RadioGroup value={prefs.theme} onValueChange={(v) => setTheme(v as ThemePref)}>
              {THEMES.map((t) => (
                <DropdownMenu.RadioItem key={t.value} value={t.value} className={cn(menuItem, 'data-[state=checked]:font-medium data-[state=checked]:text-link')}>
                  <t.icon className="size-4 text-muted-foreground" />{t.label}
                  <DropdownMenu.ItemIndicator className="ml-auto text-xs">✓</DropdownMenu.ItemIndicator>
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            {onShortcuts && (
              <DropdownMenu.Item onSelect={onShortcuts} className={menuItem}>
                <Keyboard className="size-4 text-muted-foreground" />Keyboard shortcuts
                <kbd className="ml-auto rounded border border-border px-1 text-[11px] text-muted-foreground">?</kbd>
              </DropdownMenu.Item>
            )}
            {onWhatsNew && (
              <DropdownMenu.Item onSelect={onWhatsNew} className={menuItem}>
                <Sparkles className="size-4 text-muted-foreground" />What&apos;s new
                {unseenNews && <span className="ml-auto rounded-full bg-info-subtle px-1.5 text-[11px] font-medium text-info-content">New</span>}
              </DropdownMenu.Item>
            )}
            {onWelcome && (
              <DropdownMenu.Item onSelect={onWelcome} className={menuItem}>
                <Compass className="size-4 text-muted-foreground" />Take the tour
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            <DropdownMenu.Item onSelect={handleLogout} className={cn(menuItem, 'text-danger-content data-[highlighted]:bg-danger-subtle')}>
              <LogOut className="size-4" />Log out
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </header>
  )
}
